import { useMemo } from 'react';

import { CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import confettiGif from '../../../../../../assets/lottie/confetti.gif';
import { ShoppingBagIcon } from '../../../../../../assets/svg';
import { RewardRuleEffects } from '../../../../../../types';
import { convertFromNairaToKobo } from '../../../../../../utils/helper';
import { RewardTypeIcon } from '../../../../components/RewardTypeIcon';
import { usePayLinkCart } from '../../../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../../../context/PaylinkUserContext';
import { getEarnableRewards } from '../../../../services/api';
import { TShiping } from '../../../../types/walllet';
import { formatPaylinkCart } from '../../../../utils/helper';

// import { getEarnableRewards } from "../../../../services/api";

interface IProps {
  isTitle?: boolean;
  isOrderComplete?: boolean;  
}

export const EarnableRewards = ({ isTitle = true, isOrderComplete = false }: IProps) => {
  const { adminDetails, user, shippingRate } = usePayLinkUser();
  const { storeProfile, sessionUniqueReferenceNumber, cartItems } =
    usePayLinkCart();

  const formattedCart = useMemo(
    () =>
      formatPaylinkCart(
        (cartItems || []).map((item: any) => ({
          ...item,
          image: item?.image || item?.productImages?.[0]?.documentAbsoluteUri || "",
          price: convertFromNairaToKobo(Number(item?.price || 0)),
        })),
      ),
    [cartItems],
  );

  const { data: earnableRewardsData, isLoading } = useQuery({
    queryKey: ["GetEarnableRewards", sessionUniqueReferenceNumber],
    queryFn: () =>
      getEarnableRewards({
        adminUniqueReferenceNumber:
          adminDetails?.uniqueReferenceNumber as string,
        consumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
        walletUniqueReferenceNumber:
          user?.walletUniqueReferenceNumber as string,
        merchantUniqueReferenceNumber:
          storeProfile?.merchantUniqueReferenceNumber as string,
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber as string,
        uniqueReferenceNumber: sessionUniqueReferenceNumber as string,
        cart: formattedCart,
        shipping: shippingRate as TShiping,
      }),
    enabled: !!user &&  !!storeProfile && !!sessionUniqueReferenceNumber && !!adminDetails?.uniqueReferenceNumber,
    retry: 0,
    staleTime: Infinity,
  });

  const earnableRewards = useMemo(() => {
    const rewards = earnableRewardsData?.data;
    const cartItemRewards = Array.isArray(rewards?.cartItemRewardsEarnable)
      ? rewards.cartItemRewardsEarnable
      : [];
    const cartTotalRewards = Array.isArray(rewards?.cartTotalRewardsEarnable)
      ? rewards.cartTotalRewardsEarnable
      : [];

    return [...cartItemRewards, ...cartTotalRewards];
  }, [earnableRewardsData]);

  const getRewardText = (reward: any) => {
    if (reward.rewardEffect === RewardRuleEffects.AddPoints) {
      return `${reward.rewardEffectValue} points`;
    }

    if (reward.rewardEffect === RewardRuleEffects.AddDiscount) {
      return `${reward.rewardEffectValue}% off`;
    }

    if (reward.rewardEffect === RewardRuleEffects.AddFreeItem) {
      return "Get 1 free";
    }

    if (reward.rewardEffect === RewardRuleEffects.CashBack) {
      return `${reward.rewardEffectValue || ""} cashback`;
    }

    return reward.rewardName || "Earn reward";
  };

  if (isLoading) {
    return (
      <div className="w-full h-20 flex items-center justify-center">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (earnableRewards.length === 0) {
    return null;
  }

  if (isOrderComplete) {
    return (
      <div className="text-center w-full h-full rounded-3xl bg-py_green_2-50 py-10 px-6">
        <img src={confettiGif} alt="gift bag" className="mx-auto w-20 h-20 mb-10" />
        <h5 className="!mb-4 text-base text-py_green_2-950 font-semibold leading-[120%] tracking-[-0.02em]">
          You earned rewards!
        </h5>
        <div className="w-fit mx-auto">
          <section className="flex flex-col !gap-4 w-fit">
            <ul className="flex flex-wrap !gap-2 justify-center border-y border-py_green_2-200 py-2">
              {earnableRewards.map((reward: any, index: number) => (
                <li
                  key={`${index}-${reward.uniqueReferenceNumber || reward.rewardName || "earnable"}`}
                  className="inline-flex items-center !gap-1">
                  <RewardTypeIcon rewardType={reward.rewardEffect} text="" />
                  <span className="text-xs text-py_green_1-700 leading-[140%]">
                    {getRewardText(reward)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="w-fit rounded-full p-2 flex items-center gap-2 bg-py_green_2-200 border !border-py_green_2-300">
              <ShoppingBagIcon color="#003330" />
              <p className="text-xs leading-[140%] tracking-[0] text-py_green_2-950">
                Apply earnable rewards on your next order
              </p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <section className="flex flex-col !gap-4 w-fit">
      {isTitle && (
        <h4 className="text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em]">
          Earnable rewards
        </h4>
      )}

      <ul className="flex flex-wrap !gap-2">
        {earnableRewards.map((reward: any, index: number) => (
          <li
            key={`${index}-${reward.uniqueReferenceNumber || reward.rewardName || "earnable"}`}
            className="inline-flex items-center !gap-1">
            <RewardTypeIcon rewardType={reward.rewardEffect} text="" />
            <span className="text-xs text-py_green_1-700 leading-[140%]">
              {getRewardText(reward)}
            </span>
          </li>
        ))}
      </ul>
      <div className="w-fit rounded-full p-2 flex items-center gap-2 bg-py_black-50 border border-py_black-100">
        <ShoppingBagIcon color="#84858C" />
        <p className="text-xs leading-[140%] tracking-[0] text-py_black-400">
          Apply earnable rewards on your next order
        </p>
      </div>
    </section>
  );
};
